import { isObject, set } from 'lodash'
import useLogger from './useLogger'

export interface UseGTMProps {
  currency: {
    code: string
  }
}

export interface ProductData {
  id: number,
  variation?: number,
  name: string,
  sku: string,
  price: number,
  categories: Record<string,string>[]
  stockQuantity: number
}

export const GTM_EVENTS: Record<string, string> = {
  ADD_PRODUCT_TO_CART: 'add_to_cart',
  REMOVE_PRODUCT_FROM_CART: 'remove_from_cart',
}

// @ts-expect-error
const dataLayerName = window?.gtm4wp_datalayer_name ?? 'dataLayer';

const DEFAULT_GA_BUSINESS_VERTICAL = 'retail';
const DEFAULT_GA_ITEM_ID_PREFIX = '';

const getProductCategories = (categories: Record<string, string>[]) => Object.fromEntries(
	categories.map(({name}, index) => [index === 0 ? 'item_category' : `item_category_${index}`, name])
)

// NOTE: Used in Minicart.tsx
const transformProductEventData = (product: ProductData & {quantity: number}) => {
	const productData = {
		// Basic
		item_id: product.id,
		item_name: product.name,
		item_brand: null,
		price: product.price,
		
		// Optional
		...(product?.quantity ? {quantity: product?.quantity} : {}),
		...(product?.variation ? {variant: product?.variation} : {}),
		
		...getProductCategories(product.categories)
	};
	
	// Try to set key of "google_business_vertical"
	try {
		// @ts-ignore
		set('google_business_vertical', google_business_vertical);
	} catch (err) {
		// @ts-ignore
		set('google_business_vertical', DEFAULT_GA_BUSINESS_VERTICAL);
	}
	
	// Try to set value of "google_business_vertical"
	try {
		// @ts-ignore
		set(productData.google_business_vertical, gtm4wp_id_prefix + product.id);
	} catch (err) {
		// @ts-ignore
		set(productData.google_business_vertical, DEFAULT_GA_ITEM_ID_PREFIX + product.id);
	}
	
	return productData;
}

const useGTM = ({ currency }: UseGTMProps) => {
	
	const logger = useLogger({module: 'Wooptima'});

  const pushEvent = (event: string, data: Record<string, any>) => {
		if ( ! (dataLayerName in window) || ! isObject(window[ dataLayerName ]) ) {
			logger.warn(`GTM: Datalayer "${dataLayerName}" not found.`);
			return;
		}
		
		logger.debug(`GTM: Tracking event "${event}"`, {data});
		
		// @ts-ignore
		window[ dataLayerName ].push({ event, ...data });
	}

  const addProductToCart = (product: ProductData, quantity: number) => pushEvent(GTM_EVENTS.ADD_PRODUCT_TO_CART, {
    ecommerce: {
      currency: currency.code,
			value: Number((product.price * quantity).toFixed(2)),
			items: [transformProductEventData({...product, quantity})]
    }
  })

  const removeProductFromCart = (product: ProductData, quantity: number) => pushEvent(GTM_EVENTS.REMOVE_PRODUCT_FROM_CART, {
    ecommerce: {
      currency: currency.code,
			value: Number((product.price * quantity).toFixed(2)),
			items: [transformProductEventData({...product, quantity})]
    }
  })

  return { addProductToCart, removeProductFromCart };

};

export default useGTM;